import { CalendarDays } from 'lucide-react';
import { usePosts } from '../hooks/usePosts';
import { useSiteContent } from '../hooks/useSiteContent';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { groupPosts, Post } from '../types/post';
import { Reveal } from '../components/Reveal';
import PostCard from '../components/PostCard';

function PostGrid({ title, posts }: { title: string; posts: Post[] }) {
  if (posts.length === 0) return null;
  return (
    <div>
      <h2 className="mb-6 font-display text-2xl font-semibold text-foreground md:text-3xl">{title}</h2>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {posts.map((post, i) => (
          <Reveal key={post.id} index={i} className="h-full">
            <PostCard post={post} />
          </Reveal>
        ))}
      </div>
    </div>
  );
}

/** Eventi, novità e articoli pubblicati (/novita). Titolo e introduzione da Contenuti → Eventi e novità. */
export default function Posts() {
  const { posts, loading } = usePosts();
  const { content: page, loaded } = useSiteContent('page.posts');
  // Un admin riceve anche le bozze dalla RLS: groupPosts tiene solo il pubblicato.
  const { upcoming, news, past } = groupPosts(posts);
  const empty = upcoming.length + news.length + past.length === 0;

  useDocumentMeta(page.title, page.intro);

  return (
    <div>
      <section className="bg-brand">
        <div className={`container py-16 md:py-20 ${loaded ? '' : 'invisible'}`}>
          <span className="mb-6 block h-1 w-12 rounded-full bg-[#FFB95F]" />
          <h1 className="font-display text-4xl font-bold leading-tight text-white text-balance md:text-5xl">{page.title}</h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/85 md:text-lg">{page.intro}</p>
        </div>
      </section>

      <section className="container py-14">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-t-2 border-primary" />
          </div>
        ) : empty ? (
          <div className="mx-auto max-w-md py-16 text-center">
            <CalendarDays className="mx-auto mb-4 h-10 w-10 text-primary" />
            <h2 className="mb-2 font-display text-2xl font-semibold text-foreground">Presto qualche novità</h2>
            <p className="text-muted-foreground">Qui troverai i prossimi mercatini e i racconti dall'apiario.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-14">
            <PostGrid title="Prossimi eventi" posts={upcoming} />
            <PostGrid title="Novità e articoli" posts={news} />
            <PostGrid title="Eventi passati" posts={past} />
          </div>
        )}
      </section>
    </div>
  );
}
